import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { db, auth } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { useTenant } from "../lib/tenant";
import { api, callableErrorMessage, type CashierScanCustomerQrResult } from "../lib/api";

type Tab = "scan" | "manual" | "today";

type ScanResult = CashierScanCustomerQrResult;

type Sale = {
  id: string;
  amountFinal: number;
  amountDiscount: number;
  customerUid: string;
  category: string | null;
  createdAt: Date | null;
  status: string;
  undoableUntil: Date | null;
};

const CATEGORIES_DEFAULT = ["Cafeteria", "Comida", "Bebida", "Panaderia", "Producto", "Servicio", "Otro"];

export function StaffCashier() {
  const nav = useNavigate();
  const { user, claims, loading } = useAuth();
  const { tenant } = useTenant();
  const [tab, setTab] = useState<Tab>("scan");

  if (loading) return <div className="grid min-h-dvh place-items-center bg-cream">Cargando…</div>;
  if (!user) {
    nav("/login");
    return null;
  }
  if (claims.role !== "staff" && claims.role !== "tenant_admin") {
    return (
      <main className="grid min-h-dvh place-items-center bg-cream p-6 text-center">
        <div>
          <h1 className="font-display text-2xl text-leaf-950">Acceso restringido</h1>
          <p className="mt-2 text-sm text-leaf-700">
            Esta pantalla es solo para cajeros del comercio.
          </p>
          <button onClick={() => nav("/")} className="btn-primary mt-4">
            Volver
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-dvh bg-cream pb-20">
      <header
        className="sticky top-0 z-10 bg-leaf-800 px-4 py-3 text-white"
        style={{ background: tenant?.theme.primary ?? "#3b5320" }}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/70">Cajero</p>
            <h1 className="font-display text-lg font-semibold">{tenant?.name ?? "Fideliza"}</h1>
          </div>
          <button
            onClick={() => signOut(auth)}
            className="rounded-lg bg-white/20 px-3 py-1 text-xs hover:bg-white/30"
          >
            Salir
          </button>
        </div>
        <nav className="mt-3 flex gap-1 text-xs">
          {(
            [
              { k: "scan", l: "Escanear QR" },
              { k: "manual", l: "Buscar manual" },
              { k: "today", l: "Hoy" }
            ] as const
          ).map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`flex-1 rounded-lg px-3 py-2 font-medium ${
                tab === t.k ? "bg-white text-leaf-900" : "bg-white/15 text-white"
              }`}
            >
              {t.l}
            </button>
          ))}
        </nav>
      </header>

      <div className="mx-auto max-w-md px-4 py-4">
        {tab === "scan" && <ScanTab />}
        {tab === "manual" && <ManualTab />}
        {tab === "today" && <TodayTab />}
      </div>
    </main>
  );
}

function ScanTab() {
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [manualToken, setManualToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerId = "qr-scanner-region";

  const startScanner = async () => {
    setError(null);
    setScanResult(null);
    setScanning(true);
    try {
      const html5Qr = new Html5Qrcode(containerId);
      scannerRef.current = html5Qr;
      await html5Qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        async (decoded) => {
          await stopScanner();
          await handleToken(decoded);
        },
        () => {}
      );
    } catch (err) {
      setError("No pudimos acceder a la camara. Permite el acceso o usa el campo manual.");
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch {
        /* ignore */
      }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToken = async (token: string) => {
    setError(null);
    try {
      const res = await api.cashierScanCustomerQr({ token });
      sessionStorage.setItem("fideliza:last-scan-token", token);
      setScanResult(res.data);
    } catch (err) {
      setError(callableErrorMessage(err));
    }
  };

  if (scanResult) {
    return (
      <ConfirmSaleCard
        scan={scanResult}
        onDone={() => {
          setScanResult(null);
        }}
        onCancel={() => setScanResult(null)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="card">
        <h2 className="font-display text-lg font-semibold text-leaf-950">Escanear QR del cliente</h2>
        <p className="mt-1 text-sm text-leaf-700">
          Apunta la camara al QR de la app del cliente.
        </p>

        <div
          id={containerId}
          className="mt-4 overflow-hidden rounded-2xl bg-black"
          style={{ minHeight: scanning ? 280 : 0 }}
        />

        {!scanning ? (
          <button onClick={startScanner} className="btn-primary mt-4 w-full">
            Activar camara
          </button>
        ) : (
          <button onClick={stopScanner} className="btn-ghost mt-4 w-full">
            Detener camara
          </button>
        )}
      </div>

      <div className="card">
        <h3 className="text-sm font-medium text-leaf-800">o pega el token manualmente</h3>
        <textarea
          value={manualToken}
          onChange={(e) => setManualToken(e.target.value)}
          rows={2}
          placeholder="Token QR…"
          className="input mt-2 font-mono text-xs"
        />
        <button
          disabled={!manualToken.trim()}
          onClick={() => handleToken(manualToken.trim())}
          className="btn-secondary mt-2 w-full"
        >
          Verificar token
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}

function ConfirmSaleCard({
  scan,
  onDone,
  onCancel
}: {
  scan: ScanResult;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { tenant } = useTenant();
  const [amountStr, setAmountStr] = useState("");
  const [category, setCategory] = useState<string>(CATEGORIES_DEFAULT[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ purchaseId: string; final: number; discount: number } | null>(null);

  const categories = useMemo(() => {
    const cfg = (tenant as unknown as { categories?: string[] })?.categories;
    return cfg && cfg.length > 0 ? cfg : CATEGORIES_DEFAULT;
  }, [tenant]);

  const gross = parseInt(amountStr.replace(/\D/g, ""), 10) || 0;
  const discountPct = scan.voucher?.discountPercent ?? 0;
  const discount = Math.round((gross * discountPct) / 100);
  const finalAmount = gross - discount;

  const submit = async () => {
    if (!scan.voucher && !confirm("Este cliente no tiene descuento activo. Cobrar igual?")) return;
    if (gross <= 0) {
      setError("Ingresa un monto valido.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Re-scan para obtener un token fresco no es necesario: el server validó al escanear.
      // Pero necesitamos el token original para enviar al confirm. Lo guardamos en sessionStorage
      // como workaround simple — en una segunda iteracion se podria devolver el token desde scan.
      const tok = sessionStorage.getItem("fideliza:last-scan-token");
      if (!tok) throw new Error("Token de scan expirado. Vuelve a escanear.");
      const res = await api.cashierConfirmPurchase({
        token: tok,
        amountGross: gross,
        category: category || null,
        note: note.trim() || null
      });
      setSuccess({
        purchaseId: res.data.purchaseId,
        final: res.data.amountFinal,
        discount: res.data.amountDiscount
      });
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div className="card flex flex-col items-center gap-3 text-center">
        <div className="grid h-16 w-16 place-items-center rounded-full bg-leaf-100 text-3xl text-leaf-700">
          ✓
        </div>
        <h2 className="font-display text-xl font-semibold text-leaf-950">Venta registrada</h2>
        <p className="text-sm text-leaf-700">
          Cobraste <strong>{formatCLP(success.final)}</strong>
          {success.discount > 0 && <> con descuento de {formatCLP(success.discount)}</>}
        </p>
        <UndoButton purchaseId={success.purchaseId} onDone={onDone} />
        <button onClick={onDone} className="btn-primary w-full">
          Otra venta
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="card">
        <p className="text-xs uppercase tracking-wider text-leaf-600">Cliente</p>
        <h2 className="font-display text-xl font-semibold text-leaf-950">
          {scan.customer.displayName ?? scan.customer.email ?? "Sin nombre"}
        </h2>
        <p className="mt-1 text-xs text-leaf-600">
          {scan.customer.visits} compras previas · {scan.customer.pendingTickets} tickets activos
        </p>
        {scan.voucher && (
          <div className="mt-3 rounded-xl bg-leaf-50 px-3 py-2 text-xs text-leaf-800 ring-1 ring-leaf-200">
            Voucher activo <strong>{scan.voucher.code}</strong> ·{" "}
            <strong>{scan.voucher.discountPercent}% off</strong>
            {scan.ambassadorName && <> · embajador {scan.ambassadorName}</>}
          </div>
        )}
      </div>

      <div className="card">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-leaf-700">Monto bruto (con IVA)</span>
          <input
            inputMode="numeric"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="5000"
            className="input font-mono text-xl"
          />
        </label>

        <label className="mt-3 flex flex-col gap-1 text-sm">
          <span className="text-leaf-700">Categoria</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="input"
          >
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        <label className="mt-3 flex flex-col gap-1 text-sm">
          <span className="text-leaf-700">Nota (opcional)</span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Mesa 4, retiro, etc."
            className="input"
            maxLength={80}
          />
        </label>

        {gross > 0 && (
          <div className="mt-3 rounded-2xl bg-leaf-50 p-3 text-sm">
            <div className="flex justify-between text-leaf-700">
              <span>Bruto</span>
              <span>{formatCLP(gross)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-leaf-700">
                <span>Descuento {discountPct}%</span>
                <span>-{formatCLP(discount)}</span>
              </div>
            )}
            <div className="mt-1 flex justify-between border-t border-leaf-200 pt-1 text-base font-semibold text-leaf-950">
              <span>Cobrar</span>
              <span>{formatCLP(finalAmount)}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={onCancel} className="btn-ghost">
            Cancelar
          </button>
          <button onClick={submit} disabled={busy || gross <= 0} className="btn-primary">
            {busy ? "Guardando…" : "Confirmar venta"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UndoButton({ purchaseId, onDone }: { purchaseId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async () => {
    if (!confirm("Deshacer esta venta?")) return;
    setBusy(true);
    try {
      await api.cashierUndoPurchase({ purchaseId });
      setDone(true);
      setTimeout(onDone, 600);
    } catch (err) {
      alert(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) return <p className="text-sm text-amber-700">Venta deshecha.</p>;
  return (
    <button onClick={submit} disabled={busy} className="text-xs text-red-700 underline">
      {busy ? "…" : "Deshacer (5 min)"}
    </button>
  );
}

function ManualTab() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Array<{ uid: string; displayName: string | null; email: string | null; phone: string | null }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async () => {
    if (q.trim().length < 3) {
      setError("Al menos 3 caracteres.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.cashierLookupCustomer({ query: q.trim() });
      setResults(res.data.matches);
      if (res.data.matches.length === 0) setError("Sin resultados.");
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="card">
        <h2 className="font-display text-lg font-semibold text-leaf-950">Buscar cliente</h2>
        <p className="mt-1 text-xs text-leaf-700">
          Cuando el QR no funciona, busca por email o telefono.
        </p>
        <div className="mt-3 flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="email o telefono"
            className="input"
          />
          <button onClick={search} disabled={busy} className="btn-primary">
            {busy ? "…" : "Buscar"}
          </button>
        </div>
        {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
      </div>
      {results.map((r) => (
        <div key={r.uid} className="card">
          <p className="font-medium text-leaf-950">{r.displayName ?? "Sin nombre"}</p>
          <p className="text-xs text-leaf-600">{r.email ?? r.phone ?? r.uid}</p>
          <p className="mt-2 text-[11px] text-leaf-500">
            Pide al cliente que abra su QR en la app para confirmar la venta.
          </p>
        </div>
      ))}
    </div>
  );
}

function TodayTab() {
  const { user, claims } = useAuth();
  const [sales, setSales] = useState<Sale[]>([]);

  useEffect(() => {
    if (!user || !claims.tenantId) return;
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const q = query(
      collection(db, "tenants", claims.tenantId, "purchases"),
      where("cashierUid", "==", user.uid),
      where("createdAt", ">=", Timestamp.fromDate(startOfDay)),
      orderBy("createdAt", "desc")
    );
    return onSnapshot(q, (snap) => {
      setSales(
        snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            amountFinal: data.amountFinal ?? data.amount ?? 0,
            amountDiscount: data.amountDiscount ?? 0,
            customerUid: data.userId,
            category: data.category ?? null,
            createdAt: (data.createdAt as Timestamp | undefined)?.toDate() ?? null,
            status: data.status ?? "approved",
            undoableUntil: (data.undoableUntil as Timestamp | undefined)?.toDate() ?? null
          };
        })
      );
    });
  }, [user, claims.tenantId]);

  const total = sales.filter((s) => s.status !== "undone").reduce((acc, s) => acc + s.amountFinal, 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="card">
        <p className="text-xs uppercase tracking-wider text-leaf-600">Hoy</p>
        <h2 className="font-display text-2xl font-semibold text-leaf-950">
          {formatCLP(total)} · {sales.filter((s) => s.status !== "undone").length} ventas
        </h2>
      </div>
      {sales.length === 0 && (
        <p className="text-center text-sm text-leaf-600">Sin ventas aun.</p>
      )}
      {sales.map((s) => (
        <SaleRow key={s.id} sale={s} />
      ))}
    </div>
  );
}

function SaleRow({ sale }: { sale: Sale }) {
  const canUndo = sale.status === "approved" && sale.undoableUntil && sale.undoableUntil > new Date();
  const [busy, setBusy] = useState(false);
  const undo = async () => {
    if (!confirm("Deshacer esta venta?")) return;
    setBusy(true);
    try {
      await api.cashierUndoPurchase({ purchaseId: sale.id });
    } catch (err) {
      alert(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const correct = async () => {
    const reason = prompt("Explica que pasa con esta venta:");
    if (!reason) return;
    setBusy(true);
    try {
      await api.cashierRequestCorrection({ purchaseId: sale.id, reason });
      alert("Pedido enviado al admin.");
    } catch (err) {
      alert(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card flex items-center justify-between">
      <div>
        <p className="font-medium text-leaf-950">{formatCLP(sale.amountFinal)}</p>
        <p className="text-[11px] text-leaf-500">
          {sale.createdAt?.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}
          {sale.category && ` · ${sale.category}`}
          {sale.status !== "approved" && ` · ${sale.status}`}
        </p>
      </div>
      <div className="flex gap-2">
        {canUndo && (
          <button onClick={undo} disabled={busy} className="text-xs text-red-700 underline">
            Deshacer
          </button>
        )}
        {!canUndo && sale.status === "approved" && (
          <button onClick={correct} disabled={busy} className="text-xs text-amber-700 underline">
            Pedir correccion
          </button>
        )}
      </div>
    </div>
  );
}

function formatCLP(n: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0
  }).format(n);
}
